import { CancellationNotice } from './CancellationNotice';
import { useState } from 'react';
import { RenewalNotice } from './RenewalNotice';
import type {
  AllowanceResponse,
  MyMembershipResponse,
  PlansResponse,
  SubscriptionSummary,
} from '@league/shared/contract';
import { formatMoney, formatPrice } from '@league/shared/money';
import { FOUNDING_STATUS, SUBSCRIPTION_STATUS } from '@league/shared/status';
import { StatusChip } from '@league/ui';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ChooseMembership } from '../onboarding/ChooseMembership';
import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';

/**
 * HER MEMBERSHIP, and the panel is deliberately short.
 *
 * Everything to do with a card lives in Stripe's own portal, which is one button away. Cards,
 * invoices, receipts and dunning are theirs; what she is on, what it costs, and whether she is a
 * founding director are ours. Building a second card form here would mean holding card details,
 * which the privacy page says the platform does not.
 *
 * WHAT IS NOT ON THIS SCREEN, and will not be: her founding place number. It exists so the cohort
 * can be counted and so two people paying at once cannot both take the last place. Telling her she
 * is number nineteen answers a question nobody benefits from having asked.
 *
 * The two states that need saying out loud are `past_due` and `canceled`, and they are said
 * differently on purpose. A failed card is not a lockout — Stripe is still retrying and she can
 * still run tonight's session — so it reads as something to fix rather than something that has
 * happened to her.
 */

const PANEL = 'rounded-md border border-light bg-paper px-5 py-4';

export function Membership() {
  const [resuming, setResuming] = useState(false);
  const mine = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => apiFetch<MyMembershipResponse>('/api/billing/me'),
  });

  const catalogue = useQuery({
    queryKey: ['plans'],
    queryFn: () => apiFetch<PlansResponse>('/api/public/plans'),
  });

  /* What her unfinished leagues cost her, which is the one part of the bill she never chose. */
  const allowanceQuery = useQuery({
    queryKey: ['billing', 'allowance'],
    queryFn: () => apiFetch<AllowanceResponse>('/api/billing/allowance'),
  });

  /* Stripe answers with a URL and the browser goes there. Nothing is rendered from the result. */
  const leaveForStripe = useMutation({
    mutationFn: (input: { path: string; body?: Record<string, unknown> }) =>
      apiPost<{ url: string }>(input.path, input.body ?? {}),
    onSuccess: (result) => {
      window.location.href = result.url;
    },
  });

  if (mine.isPending) return null;

  const subscription = mine.data?.subscription ?? null;
  const membership = mine.data?.membership ?? null;
  const plans = catalogue.data?.plans ?? [];
  const allowance = allowanceQuery.data?.allowance ?? null;

  const error = leaveForStripe.error instanceof ApiError ? leaveForStripe.error.message : null;

  /*
    ⚠ THERE IS NOTHING TO SWITCH BETWEEN, AND THAT IS WHY THIS PANEL IS GONE — 2026-09-15.

    A "How you pay" panel offered monthly against annual, with the saving under it. Annual was
    withdrawn, so a founding director has one membership and one price. The panel would have
    rendered a single button reading "switch to the plan you are already on".

    What replaced it is the thing she actually cannot see anywhere else: the leagues beyond her
    allowance, and what they add. It is the only part of her bill that changes without her going
    near this screen, so it is the part worth showing here.
  */
  const extra = plans.find((p) => p.key === 'extra_league_monthly');

  if (mine.data?.cancellation) return <>
    <CancellationNotice cancellation={mine.data.cancellation} firstName={mine.data.firstName} onResume={() => setResuming(true)} />
    {resuming && mine.data.cancellation.mayResume ? <ChooseMembership founding={false} /> : null}
  </>;

  if (mine.data?.renewal) return <>
    <RenewalNotice renewal={mine.data.renewal} onResume={() => setResuming(true)} />
    {resuming && mine.data.renewal.mayResume ? <ChooseMembership founding={false} /> : null}
  </>;

  if (!subscription || subscription.status === 'canceled' || subscription.status === 'incomplete') {
    return <ChooseMembership founding={membership?.founding ?? false} />;
  }

  return (
    <section>

      {error ? (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}

      {subscription ? (
        <div className="mt-3 flex flex-col gap-4">
          <div className={PANEL}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="text-[14px] font-medium">{subscription.planName}</p>
                <p className="tabular mt-1 text-[22px] font-display leading-none">
                  {formatPrice(subscription)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {membership?.founding ? <StatusChip status={FOUNDING_STATUS.founding} /> : null}
                <StatusChip status={SUBSCRIPTION_STATUS[subscription.status]} />
              </div>
            </div>

            <Explanation subscription={subscription} founding={membership?.founding ?? false} />
          </div>

          {/*
            WHAT THE LEAGUES BEYOND HER ALLOWANCE ADD.

            Hidden entirely when she is within it, rather than shown as a reassuring zero. A line
            reading "Additional leagues $0.00" invites the question of what it would take to not be
            zero, on a screen where the answer is not obvious — and a director running two leagues
            has nothing to think about here.
          */}
          {allowance && allowance.extras > 0 && extra && subscription.status !== 'comped' ? (
            <div className={PANEL}>
              <p className="text-[14px] font-medium">Additional leagues</p>
              <div className="mt-3 flex flex-wrap items-baseline justify-between gap-3">
                <p className="text-[13px] leading-relaxed text-ink-2">
                  {allowance.billingMode === 'purchased'
                    ? `Your selected allowance covers ${allowance.purchasedAllowance} leagues, including ${allowance.extras} additional ${allowance.extras === 1 ? 'league' : 'leagues'} at ${formatMoney(allowance.unitAmountCents ?? extra.amountCents, allowance.currency)} a month each.`
                    : <>You are running {allowance.active} leagues. Your membership includes {allowance.included}, so {allowance.extras === 1 ? 'one adds' : `${allowance.extras} add`} {formatMoney(extra.amountCents, extra.currency)} a month{allowance.extras === 1 ? '' : ' each'}.</>}

                </p>
                <p className="tabular font-display text-[22px] leading-none">
                  {formatMoney(allowance.extras * (allowance.unitAmountCents ?? extra.amountCents), extra.currency)}
                  <span className="ml-1 font-sans text-[13px] font-normal text-ink-2">/month</span>
                </p>
              </div>
              <p className="mt-2.5 text-[13px] text-ink-2">
                {/*
                  The reassurance that matters most, because the fear this charge creates is that it
                  compounds forever. It does not — it tracks what she is running right now.
                */}
                {allowance.billingMode === 'purchased' ? 'Finishing or archiving a league frees space in your selected allowance. It does not reduce your monthly payment.' : 'This changes as you go. Finishing or archiving a league takes its charge off your next bill.'}
              </p>
            </div>
          ) : null}

          {/* A complimentary membership has no card and no Stripe customer, so the portal would refuse. */}
          {subscription.status === 'comped' ? null : <div className={PANEL}>
            <p className="text-[14px] font-medium">Your card and receipts</p>
            <p className="mt-1.5 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
              {/*
                Said plainly, because "why do I have to leave the site" is the question this button
                otherwise raises — and the answer is one people are reassured by.
              */}
              Update your payment details, view receipts, or manage your subscription.
            </p>
            <div className="mt-3.5">
              <Button
                variant="secondary"
                disabled={leaveForStripe.isPending}
                onClick={() => leaveForStripe.mutate({ path: '/api/billing/portal' })}
              >
                Manage billing
              </Button>
            </div>
          </div>}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The sentence under the price, and it is different for every status because the thing she needs to
 * do next is different.
 */
function Explanation({
  subscription,
  founding,
}: {
  subscription: SubscriptionSummary;
  founding: boolean;
}) {
  const renews = subscription.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  if (subscription.status === 'past_due') {
    return (
      <p className="mt-3 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
        {/*
          NOT A LOCKOUT, and the copy has to carry that. Stripe retries for several days, and a
          director reading this on the afternoon of a session needs to know the room still works.
        */}
        Your last payment did not go through and Stripe is trying again. Nothing has stopped &mdash;
        your leagues, seating and scores all work as normal. Updating the card on file is what
        fixes it.
      </p>
    );
  }

  if (subscription.status === 'canceled') {
    return (
      <p className="mt-3 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
        {/*
          Two things she needs, in this order: nothing was deleted, and here is how to come back.
          Whether she keeps the founding designation is the third, and only if she has one.
        */}
        Your membership has ended. Everything you have run is still here to read and nothing has
        been deleted &mdash; you just cannot start or change a league until you subscribe again.
        {founding
          ? ' You are still a Founding Director; that does not go away.'
          : ''}
      </p>
    );
  }

  if (subscription.status === 'comped') {
    return (
      <p className="mt-3 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
        Your membership is complimentary. There is nothing to pay and no card on file.
      </p>
    );
  }

  if (subscription.status === 'incomplete') {
    return (
      <p className="mt-3 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
        You have chosen a plan but not finished paying, so this is not active yet.
      </p>
    );
  }

  return (
    <p className="mt-3 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
      {subscription.cancelAtPeriodEnd
        ? (renews ? `Renewal is canceled. Access continues through ${renews}.` : 'Renewal is canceled.')
        : (renews ? `Renews ${renews}.` : 'Active.')}
      {subscription.priceLocked
        ? ' Your founding rate is protected while your membership stays continuously active.'
        : ''}
    </p>
  );
}
